import { useMutation } from '@apollo/client';
import type { PortfolioReport } from '@interfaces/portfolioReport';
import { useToast } from 'hephaestus/web';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../hooks/useLocalization';
import {
  BUCKET_SUB_TRADE_REQUESTS_FOR_RUN,
  CANCEL_SUB_TRADE_REQUESTS_BY_ID,
  VERIFY_SUB_TRADE_REQUESTS_FOR_RUN,
} from '../queries/tradeReview.queries';
import type {
  ReviewTradeCounts,
  ReviewTradeEvaluateProcess,
  ReviewTradePortfolioGroup,
  ReviewTradeRequest,
  ReviewTradeRow,
} from '../types/tradeReview.types';
import { REVIEW_TRADE_HOLD_CLOCK_MS } from '../utils/detailPage/constants';
import {
  buildReviewEvaluateKey,
  buildReviewTradeCounts,
  buildReviewTradeRows,
  canEvaluateReviewTrades,
  groupReviewTradesByPortfolio,
} from '../utils/tradeReview/tradeReviewPresentation';

export type UseReviewTradeEvaluateParams = {
  runId: string | undefined;
  reports: PortfolioReport[];
  trades: ReviewTradeRequest[];
  tradesSettled: boolean;
  usableBulkTradeRequestId: string | undefined;
  organizationId: string | undefined;
  requireApproval: boolean;
  process: ReviewTradeEvaluateProcess;
  canWriteSubTradeRequest: boolean;
  refetchTrades: () => Promise<unknown>;
};

export type UseReviewTradeEvaluateResult = {
  rows: ReviewTradeRow[];
  groups: ReviewTradePortfolioGroup[];
  counts: ReviewTradeCounts;
  evaluated: boolean;
  evaluating: boolean;
  canEvaluate: boolean;
  cancelling: boolean;
  verifying: boolean;
  onCancelTrades: (ids: string[]) => Promise<void>;
  onApproveTrades: (ids: string[]) => Promise<void>;
};

export const useReviewTradeEvaluate = ({
  runId,
  reports,
  trades,
  tradesSettled,
  usableBulkTradeRequestId,
  organizationId,
  requireApproval,
  process,
  canWriteSubTradeRequest,
  refetchTrades,
}: UseReviewTradeEvaluateParams): UseReviewTradeEvaluateResult => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['bulkRebalance']);
  const { enqueueToast } = useToast();
  const evaluatedKeyRef = useRef<string | null>(null);
  const [hasEvaluated, setHasEvaluated] = useState(false);
  const [now, setNow] = useState(() => new Date());

  const [bucketSubTradeRequests, { loading: bucketing }] = useMutation(
    BUCKET_SUB_TRADE_REQUESTS_FOR_RUN,
  );
  const [cancelSubTradeRequestsById, { loading: cancelling }] = useMutation(
    CANCEL_SUB_TRADE_REQUESTS_BY_ID,
  );
  const [verifySubTradeRequests, { loading: verifying }] = useMutation(
    VERIFY_SUB_TRADE_REQUESTS_FOR_RUN,
  );

  const reportsById = useMemo(
    () => new Map(reports.map((report) => [report.id, report])),
    [reports],
  );

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNow(new Date());
    }, REVIEW_TRADE_HOLD_CLOCK_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  const rows = useMemo(
    () => buildReviewTradeRows(trades, hasEvaluated, now, reportsById),
    [hasEvaluated, now, reportsById, trades],
  );
  const counts = useMemo(() => buildReviewTradeCounts(rows), [rows]);
  const groups = useMemo(
    () =>
      groupReviewTradesByPortfolio(
        translateBackendString,
        rows,
        reportsById,
        t('reviewTrades.unknownPortfolio'),
      ),
    [reportsById, rows, t],
  );

  const actionableIds = useMemo(
    () => rows.filter((row) => row.isActionable).map((row) => row.id),
    [rows],
  );
  const evaluateKey = buildReviewEvaluateKey({
    runId,
    bulkTradeRequestId: usableBulkTradeRequestId,
    actionableIds,
    allActionableVerified: counts.unverifiedActionable === 0,
    process,
  });
  const canEvaluate = canEvaluateReviewTrades({
    bulkTradeRequestId: usableBulkTradeRequestId,
    tradesSettled,
    fetchedCount: trades.length,
    actionableCount: counts.actionable,
    process,
    requireApproval,
    unverifiedActionableCount: counts.unverifiedActionable,
    canWriteSubTradeRequest,
  });
  const evaluated =
    hasEvaluated &&
    evaluateKey != null &&
    evaluatedKeyRef.current === evaluateKey;

  useEffect(() => {
    evaluatedKeyRef.current = null;
    setHasEvaluated(false);
  }, [runId]);

  useEffect(() => {
    if (evaluateKey && evaluatedKeyRef.current !== evaluateKey) {
      setHasEvaluated(false);
    }
  }, [evaluateKey]);

  useEffect(() => {
    if (!canEvaluate || !evaluateKey || !usableBulkTradeRequestId) {
      return;
    }

    if (evaluatedKeyRef.current === evaluateKey) {
      return;
    }

    let cancelled = false;

    const evaluate = async () => {
      try {
        await bucketSubTradeRequests({
          variables: {
            input: {
              bulkTradeRequestId: usableBulkTradeRequestId,
              ...(organizationId && { organizationId }),
            },
          },
        });

        if (cancelled) {
          return;
        }

        evaluatedKeyRef.current = evaluateKey;
        setHasEvaluated(true);
        await refetchTrades();
      } catch (error) {
        if (cancelled) {
          return;
        }

        enqueueToast(
          error instanceof Error
            ? error.message
            : t('reviewTrades.evaluateError'),
          { variant: 'error' },
        );
      }
    };

    void evaluate();

    return () => {
      cancelled = true;
    };
  }, [
    bucketSubTradeRequests,
    canEvaluate,
    enqueueToast,
    evaluateKey,
    organizationId,
    refetchTrades,
    t,
    usableBulkTradeRequestId,
  ]);

  const onCancelTrades = useCallback(
    async (ids: string[]) => {
      if (!ids.length) {
        return;
      }

      try {
        await cancelSubTradeRequestsById({
          variables: {
            input: {
              subTradeRequestIds: ids,
              ...(organizationId && { organizationId }),
            },
          },
        });
        enqueueToast(t('reviewTrades.cancelSuccess', { count: ids.length }), {
          variant: 'success',
        });
        await refetchTrades();
      } catch (error) {
        enqueueToast(
          error instanceof Error
            ? error.message
            : t('reviewTrades.cancelError'),
          { variant: 'error' },
        );
      }
    },
    [
      cancelSubTradeRequestsById,
      enqueueToast,
      organizationId,
      refetchTrades,
      t,
    ],
  );

  const onApproveTrades = useCallback(
    async (ids: string[]) => {
      if (!ids.length || !organizationId) {
        return;
      }

      try {
        await verifySubTradeRequests({
          variables: {
            input: {
              subTradeRequestIds: ids,
              organizationId,
            },
          },
        });
        enqueueToast(t('reviewTrades.approveSuccess', { count: ids.length }), {
          variant: 'success',
        });
        await refetchTrades();
      } catch (error) {
        enqueueToast(
          error instanceof Error
            ? error.message
            : t('reviewTrades.approveError'),
          { variant: 'error' },
        );
      }
    },
    [enqueueToast, organizationId, refetchTrades, t, verifySubTradeRequests],
  );

  return {
    rows,
    groups,
    counts,
    evaluated,
    evaluating: bucketing,
    canEvaluate,
    cancelling,
    verifying,
    onCancelTrades,
    onApproveTrades,
  };
};
